<script setup lang="ts">
import { nextTick, ref } from 'vue';

import KTextField, { type FieldSize } from '@/components/fields/KTextField.vue';
import useField from '@/composables/useField';
import useNumberField from '@/composables/useNumberField';

export interface Props {
  modelValue?: number | null;
  label?: string;
  size?: FieldSize;
  min?: number;
  max?: number;
  clearable?: boolean;
  readonly?: boolean;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  label: undefined,
  size: 'md',
  min: 1,
  max: 9999,
  clearable: false,
  readonly: false,
  disabled: false,
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref<string | number | null | undefined>(props.modelValue);

useField(props, fieldValue, (newFieldValue) => {
  if (props.clearable && !newFieldValue?.toString()) {
    emit('update:modelValue', null);
  } else {
    emit('update:modelValue', Number(newFieldValue) || props.min);
  }
});

const { isError, blinkError, handleKeypress } = useNumberField();

function _clearFieldValue(rewriteValue: string): void {
  fieldValue.value = null;
  void nextTick(() => {
    fieldValue.value = rewriteValue;
  });
}

function handleInput(event: InputEvent): void {
  // @ts-expect-error
  const value = event.target?.value;
  if (props.clearable && !value) return;
  const valueNum = Number(value);
  const valueInt = parseInt(value);
  const valueIntStr = String(valueInt);
  let rewriteValue = '';
  if (!value || valueNum < props.min || valueNum !== valueInt) {
    rewriteValue = String(props.min);
  } else if (valueNum > props.max) {
    rewriteValue = String(props.max);
  } else if (valueIntStr !== value) {
    rewriteValue = valueIntStr;
  }
  if (rewriteValue) {
    blinkError();
    _clearFieldValue(rewriteValue);
  }
}
</script>

<template>
  <KTextField
    v-model.trim="fieldValue"
    :class="$style.kNumberField"
    type="number"
    :label="props.label"
    :size="props.size"
    :min="props.min"
    :max="props.max"
    :clearable="props.clearable"
    :readonly="props.readonly"
    :disabled="props.disabled"
    inputmode="numeric"
    step="1"
    :error="isError"
    data-testid="kNumberField"
    @input="handleInput"
    @keypress="handleKeypress"
  />
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.kNumberField {
}
</style>
